<template>
  <div
    :class="['cf-menu-list__item', { 'cf-menu-list__item--favorite': isFavorite, 'cf-menu-list__item--image-right': isImageRight }]"
    @click="menuItemClickHandler"
  >
    <div class="cf-menu-list__item-banners">
      <div
        v-if="item.isNewItem"
        class="cf-menu-list-item-banner cf-menu-list-item-banner--new-banner"
      >
        New!
      </div>

      <div
        v-if="item.isLowInventory"
        class="cf-menu-list-item-banner cf-menu-list-item-banner--low-stock-banner"
      >
        Low Stock
      </div>
    </div>

    <img
      v-if="imageUrl"
      class="cf-menu-list__item-image"
      :src="imageUrl"
      onerror="this.src='/images/image-not-found.png';"
    >

    <div :class="['cf-menu-list__item-description', { 'cf-menu-list__item-description--no-image': !imageUrl }]">
      <div :class="['cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10', { 'cf-full-width': !imageUrl }]">
        <h4 class="cf-title cf-title--4 cf-no-wrap-ellipsis-multi-line">
          {{ item.displayName }}
        </h4>
      </div>

      <div
        v-if="isItemRatingDisplayed"
        class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-4"
      >
        <span class="cf-subtitle cf-subtitle--2">
          {{ itemRating }}
        </span>

        <span class="cf-icon cf-icon--smaller cf-icon--red fa fa-star" />
      </div>

      <p
        v-if="item.descriptionText"
        :class="['cf-subtitle cf-subtitle--3 cf-no-wrap-ellipsis-multi-line']"
      >
        {{ item.descriptionText }}
      </p>

      <!-- TODO: Figure out digital menu board display when read only is true, but read only for table top device -->
      <!-- <p
        v-if="isReadOnly && item.modifierSummary"
        class="cf-subtitle cf-subtitle--3"
      >
        {{ item.modifierSummary }}
      </p> -->

      <h5
        v-show="selectedLocation.storeId && !selectedLocation.features.HidePrices && !(itemPrice === 0 && merchant.options.hideZeroPricedItemPrices)"
        class="cf-title cf-title--5"
      >
        {{ $dollars(itemPrice) }}
      </h5>
    </div>

    <div
      v-if="isFavorite"
      class="cf-menu-list-favorite-button-wrapper"
    >
      <cf-favorite-button
        disable-state-update
        :item="item"
        :modifiers="[]"
        :special-instructions="item.specialInstructions"
      />
    </div>

    <div class="cf-menu-list-add-to-cart-button-wrapper">
      <button
        v-if="isQuickAddEnabled"
        :class="['cf-button cf-button--fixed-40', { 'cf-button--loading': isAddingItem }]"
        type="button"
        title="Add to Cart"
        :disabled="order.isAddingItem"
        @click.stop="quickAddClickHandler"
      >
        <span class="cf-icon cf-icon--brand-primary far fa-plus" />

        <span class="ada-text">
          Add to Cart
        </span>
      </button>

      <button
        v-else-if="isReadOnlyOrdable"
        :class="['cf-button cf-button--fixed-40', { 'cf-button--loading': isAddingItem }]"
        type="button"
        title="View Details"
        :disabled="order.isAddingItem"
        @click.stop="viewItem"
      >
        <span class="cf-icon cf-icon--brand-primary far fa-plus" />

        <span class="ada-text">
          View Details
        </span>
      </button>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import { generateMenuItemHash } from '@/helpers/menu-hash-helper';

  import CfFavoriteButton from '@/view/components/shared/cf-favorite-button/index.vue';


  export default {
    name: 'CfMenuListItem',

    components: {
      CfFavoriteButton
    },

    props: {
      item: {
        type: Object,
        required: true
      },

      categoryId: {
        type: Number,
        default: undefined
      },

      isFavorite: {
        type: Boolean,
        default: false
      },

      isReadOnly: {
        type: Boolean,
        default: false
      },

      showItemRating: {
        type: Boolean,
        default: true
      },

      isMenuView: {
        type: Boolean,
        default: true
      }
    },

    data() {
      return {
        isAddingItem: false
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu', 'selectedCategory']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['session', 'orderDetails']),

      isOrderAtTable() {
        return this.orderDetails.orderType === 'table';
      },

      isReadOnlyOrderAtTable() {
        const isReadOnlyOrderAtTable = this.isOrderAtTable && this.isReadOnly;

        return isReadOnlyOrderAtTable;
      },

      isReadOnlyOrdable() {
        if (this.isReadOnlyOrderAtTable) {
          return this.item.readOnlyOrderable ?? false;
        }

        return !this.isReadOnly;
      },

      isQuickAddEnabled() {
        return this.selectedLocation.isReadyForOrdering
          && this.selectedLocation.storeId
          && this.selectedLocation.isCurrentServiceAvailable
          && !this.menu.unavailablePerSchedule
          && this.isReadOnlyOrdable
          && (this.isFavorite || this.item.quickAddEnabled);
      },

      isAgeVerificationRequired() {
        return this.item.ageVerificationTemplate === 'AlcoholTemplate' && !this.session.hasVerifiedAgeForAlcohol;
      },

      imageUrl() {
        const image = this.item.images.find(img => img.label === 'menu');

        return image ? image.url : null;
      },

      isImageRight() {
        return Boolean(this.merchant.features['align-menu-image-right']);
      },

      itemQuantity() {
        const addedUpsellItem = this.order.orderItems.find(orderItem => orderItem.menuItemId === this.item.menuItemId);

        return addedUpsellItem ? addedUpsellItem.quantity : 0;
      },

      itemRating() {
        return this.item.averageRating?.toFixed(1);
      },

      isItemRatingDisplayed() {
        const isMerchantMenuItemRatingEnabled = this.merchant.features['item-rating-enabled'];

        const hasItemRatingMetReviewThreshold = this.itemRating && this.itemRating >= this.merchant.options.menuRatingThreshold && this.showItemRating;

        return isMerchantMenuItemRatingEnabled && hasItemRatingMetReviewThreshold;
      },

      itemPrice() {
        return this.item.pricePerUnitAfterMods || this.item.price;
      },

      isFavoritedItemUnavailable() {
        if (this.isFavorite && !this.isMenuView) {
          /* Important!
           * Favorited items displayed on favorites page reference different list data than that of the menu page.
           * The Menu list item's filter out unavailble items, while the Favorites page shows all favorited items despite their availabilty.
           * See: store['favoriteItems/favoriteItems'] and store['menu/menu'] for details.
           */
          return this.item.currentlyUnavailable || !this.item.allModifiersAvailable;
        }

        return false;
      }
    },

    methods: {
      menuItemClickHandler() {
        if (this.isFavoritedItemUnavailable) {
          this.openUnavailableFavoriteItem();
        }
        else if (this.isAgeVerificationRequired) {
          this.openAgeVerificationModal({ userAction: 'viewItem' });
        }
        else {
          this.viewItem();
        }
      },

      openUnavailableFavoriteItem() {
        this.$openModal('unavailableFavoriteItemModal');
      },

      openAgeVerificationModal({ userAction }) {
        const confirmHandler = userAction === 'addItem' ? this.addItem.bind(this) : this.viewItem.bind(this);

        const ageVerificationPayload = {
          onConfirm: confirmHandler,
          required: false
        };

        this.$openModal('ageVerificationModal', ageVerificationPayload);
      },

      quickAddClickHandler() {
        if (this.isAgeVerificationRequired) {
          this.openAgeVerificationModal({ userAction: 'addItem' });
        }
        else {
          this.addItem();
        }
      },

      async addItem() {
        try {
          this.isAddingItem = true;

          if (this.isFavorite) {
            await this.addFavoriteItemToOrder();
          }
          else {
            await this.addItemToOrder();
          }
        }
        catch (error) {
          errorHandlers.silent(error);
        }
        finally {
          this.isAddingItem = false;
        }
      },

      async addFavoriteItemToOrder() {
        try {
          const addFavoriteItemPayload = { item: this.item };

          await this.$store.dispatch('favoriteItems/addFavoriteItemToOrder', addFavoriteItemPayload);

          // this.$triggerGoogleTrackingEvent('AddFavoriteItemToCart', 'Favorites');

          this.$triggerNewRelicEvent('AddFavoriteItemToCart', 'Information', 'Added Favorite Item To Cart');
        }
        catch (error) {
          throw error;
        }
      },

      async addItemToOrder() {
        try {
          const addItemPayload = this.formatAddToOrderData();

          await this.$store.dispatch('orders/addItem', addItemPayload);

          // this.$triggerGoogleTrackingEvent('AddItemToCart', 'Order');

          this.$triggerNewRelicEvent('AddItemToCart', 'Information', 'Added Item To Cart');
        }
        catch (error) {
          throw error;
        }
      },

      viewItem() {
        const selectItemPayload = this.getSelectedItemData();

        this.$store.dispatch('menu/selectItem', selectItemPayload);

        this.$store.dispatch('menu/selectCategory', this.categoryId);

        if (this.isMenuView) {
          this.goToMenuItemDetailsView();
        }
        else {
          this.openMenuItemModal();
        }

        const trackingPayload = { itemInfo: [{ ...this.item, categoryId: this.categoryId }] };

        this.$googleEventTracking.postEvent('view_item', trackingPayload);

        // this.$triggerGoogleTrackingEvent('ViewMenuItem', 'Menu');

        this.$triggerNewRelicEvent('ViewMenuItem', 'Information', 'View Menu Item');
      },

      goToMenuItemDetailsView() {
        const appRoute = this.isOrderAtTable ? 'orderAtTableMenuItemDetailsPage' : 'orderAheadMenuItemDetailsPage';

        const routerPayload = {
          name: appRoute,
          params: { merchantId: this.$route.params.merchantId, menuItemId: this.item.menuItemId },
          query: { favoriteItemId: this.item.favoriteItemId }
        };

        if (appRoute === 'orderAheadMenuItemDetailsPage' && this.item.displayName) {
          routerPayload.hash = generateMenuItemHash(this.item.displayName);
        }

        /** Note:
         * If the menu item detail route view is rendered from an actual user click, manually set route parameters which are passed to the store, then open modal.
         * However, if the menu page is refreshed or a deep link with menu item id param, then set the selected menu item id via the store, then open modal.
         * See: menu-item-detail-page.vue (ie. CfMenuItemDetailPage ) for details.
         */
        this.$router.push(routerPayload);
      },

      openMenuItemModal() {
        let isReadOnly;

        if (this.isMenuView && this.isReadOnlyOrderAtTable) {
          const isNotOrdable = !this.item.readOnlyOrderable ?? true;

          isReadOnly = isNotOrdable;
        }
        else {
          isReadOnly = false;
        }

        this.$openModal('menuItem', { isReadOnly });
      },

      getSelectedItemData() {
        const selectItemPayload = { itemId: this.item.menuItemId };

        if (this.isFavorite) {
          Object.assign(selectItemPayload, { favoriteItemId: this.item.favoriteItemId });
        }

        if (this.item.isUpsell) {
          Object.assign(selectItemPayload, { isUpsell: this.item.isUpsell });

          this.$triggerNewRelicEvent('UpsellItemViewed', 'Information', 'Upsell Item Viewed');
        }

        return selectItemPayload;
      },

      formatAddToOrderData() {
        let item;
        /** Note:
         * If an item has item groups, then it has dynamic modifiers.
         * If it has dynamic modifiers parse the modifier groups to set
         * default modifier selections.
         * See: cf-modifier-groups component for similar logic.
         */
        if (this.item.itemGroups) {
          item = this.getDynamicItemData(this.item);
        }

        else {
          item = this.getItemData(this.item);
        }

        return { item };
      },

      getDynamicItemData(item) {
        const itemData = {};

        const dynamicGroups = item.itemGroups;

        item.modifierGroups.forEach((group) => {
          /** Note:
           * If the group is virtual then it has virtual modifiers that affect the rendering of the other modifiers.
           * There is a default modifier, so find and  reference its id within item groups to get its corresponding modifiers.
           */
          if (group.isVirtual) {
            const defaultDynamicModifier = group.modifiers.find(modifier => modifier.isDefault);

            if (defaultDynamicModifier) {
              const modifierGroups = dynamicGroups[defaultDynamicModifier.menuItemGroupItemId];

              const data = {
                categoryId: this.categoryId,
                name: item.displayName,
                isUpsell: item.isUpsell,
                maxAllowed: item.maxAllowed,
                menuItemId: defaultDynamicModifier.menuItemGroupItemId,
                price: defaultDynamicModifier.price,
                modifiers: this.getDefaultModifierGroups(modifierGroups),
                quantity: 1
              };

              Object.assign(itemData, data);
            }
          }
        });

        return itemData;
      },

      getItemData(item) {
        const itemData = {
          categoryId: this.categoryId,
          name: item.displayName,
          isUpsell: item.isUpsell,
          maxAllowed: item.maxAllowed,
          menuItemId: item.menuItemId,
          price: item.price,
          modifiers: [],
          quantity: 1
        };

        if (item.modifierGroups.length) {
          const defualtMods = this.getDefaultModifierGroups(item.modifierGroups);

          Object.assign(itemData, { modifiers: defualtMods });
        }

        return itemData;
      },

      getDefaultModifierGroups(modifierGroups) {
        const mods = [];

        modifierGroups.forEach((group) => {
          group.modifiers.forEach((modifier) => {
            if (modifier.isDefault) {
              mods.push({ menuItemId: modifier.menuItemId, quantity: 1 });
            }
          });
        });

        return mods;
      }
    }
  };
</script>
